/**
 * Hook: useReferralTracking
 *
 * Reads ?ref=CODE on any page load. If present:
 *   1. Stores the code in localStorage under 'veevammerce_ref' (90-day TTL)
 *   2. Pings /api/affiliate/track-click so we can show the affiliate their click count
 *
 * The code is later included in the checkout request so we can attribute the sale.
 */
import { useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'veevammerce_ref';
const TTL_MS = 1000 * 60 * 60 * 24 * 90; // 90 days

export function setReferralCode(code) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch (e) {
    console.debug('Could not persist referral code:', e?.message || e);
  }
}

export function getReferralCode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.code) return null;
    if (Date.now() - (parsed.ts || 0) > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch (e) {
    console.debug('Could not read referral code:', e?.message || e);
    return null;
  }
}

export function clearReferralCode() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.debug('Could not clear referral code:', e?.message || e); }
}

export function useReferralTracking() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || '').toUpperCase().trim();
    if (!ref) return;

    setReferralCode(ref);

    // Capture the page that sent the visitor (social, blog, etc.) — used for
    // admin source attribution. document.referrer can be empty in some cases
    // (privacy modes, direct nav) which is fine.
    // NOTE: ref code + source URL are NON-SENSITIVE marketing attribution data;
    // localStorage is acceptable here. Do NOT store auth tokens, PII, or secrets
    // in localStorage anywhere in this app — use httpOnly cookies for that.
    const sourceUrl = document.referrer || '';
    try {
      localStorage.setItem('veevammerce_ref_source', sourceUrl);
    } catch (e) {
      console.debug('Could not persist referral source URL:', e?.message || e);
    }

    // Fire-and-forget — we don't surface failures to the user
    axios.post(`${API}/affiliate/track-click`, {
      code: ref,
      landing_url: window.location.pathname + window.location.search,
      source_url: sourceUrl,
    }).catch((err) => console.debug('Click tracking failed:', err?.message || err));
  }, []);
}

export function getReferralSourceUrl() {
  try { return localStorage.getItem('veevammerce_ref_source') || ''; } catch { return ''; }
}
